// =================================================================
// PreviewContextProvider
//
// PREVIEW — remove when no longer needed.
//
// Supplies a mock brain-health identity through the SAME context that
// useCurrentContext reads, so the onboarding pages (Assessment,
// AssessmentResults, Goals) render end-to-end with no Firebase auth
// and no database writes. Used only by the /assessment/preview routes.
//
// This deliberately bypasses CurrentContextProvider (which pulls from
// the real auth sub-hooks) and RequireBrainHealthUser (the guard).
// The pages can't tell the difference because they only ever read the
// context value.
//
// Teardown: delete this file, remove the three /assessment/preview
// routes in App.jsx, and remove the `isPreview` branches in
// Assessment.jsx, AssessmentResults.jsx, and Goals.jsx (all tagged
// "PREVIEW — remove when no longer needed").
// =================================================================

import { useMemo } from 'react';
import {
  CurrentContextContext,
  CONTEXT_TYPES,
} from '../lib/useCurrentContext.jsx';

// A self-contained mock account. Shaped exactly like the unified
// `record` that the real provider exposes (snake_case fields, an
// assessment stub so Goals doesn't redirect back to the assessment).
const PREVIEW_ACCOUNT = {
  id: 'preview-user',
  display_name: 'Preview User',
  // Goals.jsx checks `account.assessment?.current` and bounces back
  // to the assessment if it's missing. Supplying a stub keeps the
  // preview goals page reachable directly if someone deep-links it,
  // though the normal flow arrives carrying live results in router
  // state.
  assessment: {
    current: {
      score: 0,
      level: 'standard',
      date: 0,
    },
  },
  goals: [],
};

export default function PreviewContextProvider({ children }) {
  const value = useMemo(
    () => ({
      loading: false,
      type: CONTEXT_TYPES.INDIVIDUAL_USER,
      id: PREVIEW_ACCOUNT.id,
      displayName: PREVIEW_ACCOUNT.display_name,
      entitlementOk: true,
      // No real RTDB path. The pages' preview branches skip all writes,
      // but if any write slipped through, this obviously-fake path
      // would fail loudly rather than touch real data.
      rtdbPath: null,
      record: PREVIEW_ACCOUNT,
      raw: null,
      // Marker the pages read to switch into no-write preview behavior.
      isPreview: true,
    }),
    []
  );

  return (
    <CurrentContextContext.Provider value={value}>
      {children}
    </CurrentContextContext.Provider>
  );
}
